import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../interceptors";

const rowVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0 },
};

const ago = (h) => new Date(Date.now() - h * 3600000).toISOString();
const SEED_AUDIT = [
  { id: "a1", toolName: "job_create", action: "Created job opening: Senior React Developer", user: "Priya Sharma", createdAt: ago(1) },
  { id: "a2", toolName: "invite_send", action: "Invited 12 candidates for Data Scientist role", user: "Priya Sharma", createdAt: ago(2) },
  { id: "a3", toolName: "interview_score", action: "Interview scored: Aarav Sharma — 85/100 for Senior React Developer", user: "System AI", createdAt: ago(3) },
  { id: "a4", toolName: "job_update", action: "Added 8 questions to Java Backend Developer interview", user: "Admin User", createdAt: ago(4) },
  { id: "a5", toolName: "interview_complete", action: "Candidate Priya Nair completed interview for Data Scientist", user: "System", createdAt: ago(5) },
  { id: "a6", toolName: "invite_send", action: "Sent WhatsApp reminders to 8 pending candidates", user: "System", createdAt: ago(6) },
  { id: "a7", toolName: "interview_score", action: "Interview scored: Vikram Reddy — 72/100 for DevOps Engineer", user: "System AI", createdAt: ago(8) },
  { id: "a8", toolName: "job_create", action: "Created job opening: QA Automation Engineer — 4 positions", user: "Admin User", createdAt: ago(10) },
  { id: "a9", toolName: "system_config", action: "System: AI scoring completed for batch #47 — 18 interviews processed", user: "System", createdAt: ago(12) },
  { id: "a10", toolName: "invite_send", action: "Invited 25 candidates for QA Automation Engineer role", user: "Priya Sharma", createdAt: ago(14) },
  { id: "a11", toolName: "interview_complete", action: "Candidate Suresh Kumar completed interview for Java Backend Developer", user: "System", createdAt: ago(18) },
  { id: "a12", toolName: "job_update", action: "Updated salary range for Cloud Solutions Architect: 35-70 LPA", user: "Admin User", createdAt: ago(20) },
  { id: "a13", toolName: "interview_score", action: "Interview scored: Arjun Krishnan — 91/100 for Data Scientist", user: "System AI", createdAt: ago(24) },
  { id: "a14", toolName: "system_audit", action: "System: Daily backup completed — 156 records archived", user: "System", createdAt: ago(26) },
  { id: "a15", toolName: "invite_send", action: "Invited 15 candidates for Full Stack Developer (Node+React)", user: "Priya Sharma", createdAt: ago(30) },
  { id: "a16", toolName: "job_create", action: "Created job opening: Product Manager — 2 positions in Mumbai, Bengaluru", user: "Admin User", createdAt: ago(36) },
  { id: "a17", toolName: "resume_upload", action: "Bulk uploaded 30 resumes — 28 parsed successfully, 2 failed", user: "Priya Sharma", createdAt: ago(40) },
  { id: "a18", toolName: "interview_complete", action: "Candidate Rahul Singh completed interview for DevOps Engineer", user: "System", createdAt: ago(44) },
  { id: "a19", toolName: "system_config", action: "System: Email service health check — all endpoints operational", user: "System", createdAt: ago(48) },
  { id: "a20", toolName: "invite_send", action: "Re-sent interview invites to 5 candidates with expired links", user: "Priya Sharma", createdAt: ago(52) },
];

/* ── Category tabs ── */
const TABS = [
  { key: "all",        label: "All",        match: () => true },
  { key: "invites",    label: "Invites",    match: (r) => /invite/i.test(r.toolName || "") },
  { key: "interviews", label: "Interviews", match: (r) => /interview/i.test(r.toolName || "") },
  { key: "jobs",       label: "Jobs",       match: (r) => /job/i.test(r.toolName || "") },
  { key: "system",     label: "System",     match: (r) => /system|config|setting|audit/i.test(r.toolName || "") },
];

/* ── Relative time helper ── */
const relativeTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

/* ── Icon for tool category ── */
const toolIcon = (toolName) => {
  const t = (toolName || "").toLowerCase();
  if (t.includes("invite")) return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
    </svg>
  );
  if (t.includes("interview")) return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
  );
  if (t.includes("job")) return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
    </svg>
  );
  if (t.includes("resume")) return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
    </svg>
  );
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
};

const toolColor = (toolName) => {
  const t = (toolName || "").toLowerCase();
  if (t.includes("invite")) return "bg-purple-500";
  if (t.includes("interview")) return "bg-blue-500";
  if (t.includes("job")) return "bg-yellow-500";
  if (t.includes("resume")) return "bg-green-500";
  return "bg-vf-primary";
};

const PAGE_SIZE = 20;

const Audit = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [viewMode, setViewMode] = useState("timeline"); // "timeline" | "table"
  const [page, setPage] = useState(0);

  useEffect(() => { document.title = "Activity Feed - Ample Hire"; }, []);

  useEffect(() => {
    setRows(SEED_AUDIT);
    setLoading(false);
  }, []);

  const filtered = useMemo(() => {
    const tab = TABS.find((t) => t.key === activeTab) || TABS[0];
    return rows.filter(tab.match);
  }, [rows, activeTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when tab changes
  useEffect(() => { setPage(0); }, [activeTab]);

  return (
    <div className="max-w-5xl mx-auto space-y-6 font-body">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-2xl font-bold text-vf-primary font-heading">
          Activity Feed
        </h1>
        <p className="text-sm mt-1 text-vf-muted">
          Every action the AI assistant takes on your tenant. 200 most recent.
        </p>
      </motion.div>

      {/* Tabs & View Toggle */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-1">
          {TABS.map((tab) => {
            const count = rows.filter(tab.match).length;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`btn-sm px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeTab === tab.key
                    ? "btn-primary"
                    : "btn-ghost"
                }`}
              >
                {tab.label}
                {count > 0 && (
                  <span className={`ml-1.5 text-2xs ${activeTab === tab.key ? "opacity-80" : "text-vf-muted"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1 bg-vf-tint rounded-lg p-0.5">
          <button
            onClick={() => setViewMode("timeline")}
            className={`btn-sm px-2.5 py-1 text-xs rounded-md transition-colors ${
              viewMode === "timeline" ? "bg-white text-vf-base shadow-sm" : "text-vf-muted"
            }`}
          >
            Timeline
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`btn-sm px-2.5 py-1 text-xs rounded-md transition-colors ${
              viewMode === "table" ? "bg-white text-vf-base shadow-sm" : "text-vf-muted"
            }`}
          >
            Table
          </button>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="card overflow-hidden"
      >
        {loading ? (
          <div className="p-10 text-center">
            <div className="skeleton h-4 w-24 mx-auto rounded" />
          </div>
        ) : paged.length === 0 ? (
          <div className="p-10 text-center text-sm empty-state text-vf-muted">
            No actions found{activeTab !== "all" ? ` in "${TABS.find((t) => t.key === activeTab)?.label}"` : ""}.
          </div>
        ) : viewMode === "timeline" ? (
          /* ── Timeline View ── */
          <div className="p-5">
            <div className="relative ml-3">
              {/* Vertical line */}
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-vf-surface" />
              <AnimatePresence mode="popLayout">
                <ul className="space-y-4">
                  {paged.map((r, i) => (
                    <motion.li
                      key={r.id}
                      custom={i}
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      className="relative flex items-start gap-4 pl-7"
                    >
                      {/* Colored dot */}
                      <span className={`absolute left-0 top-1 w-[15px] h-[15px] rounded-full flex items-center justify-center text-white ${toolColor(r.toolName)}`}>
                        <span className="scale-75">{toolIcon(r.toolName)}</span>
                      </span>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-vf-base">
                            {r.summary || r.toolName || "Action"}
                          </span>
                          <code className="badge-brand text-2xs">{r.toolName}</code>
                          <span className={r.outcome === "ok" ? "badge-success" : "badge-danger"}>
                            {r.outcome || "\u2014"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11px] text-vf-muted">{r.actorUserId}</span>
                          <span className="text-[10px] text-vf-muted">{relativeTime(r.createdAt)}</span>
                        </div>
                        {r.summary && r.toolName && r.summary !== r.toolName && (
                          <p className="text-xs mt-1 text-vf-muted leading-relaxed">{r.summary}</p>
                        )}
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </AnimatePresence>
            </div>
          </div>
        ) : (
          /* ── Table View ── */
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="tbl-header">
                  <th className="text-left px-4 py-3 w-[18%] font-heading">When</th>
                  <th className="text-left px-4 py-3 w-[20%] font-heading">User</th>
                  <th className="text-left px-4 py-3 w-[16%] font-heading">Tool</th>
                  <th className="text-left px-4 py-3 w-[10%] font-heading">Outcome</th>
                  <th className="text-left px-4 py-3 w-[36%] font-heading">Summary</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r, i) => (
                  <motion.tr
                    key={r.id}
                    custom={i}
                    variants={rowVariants}
                    initial="hidden"
                    animate="visible"
                    className="tbl-row border-b border-vf-surface"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs text-vf-muted" title={new Date(r.createdAt).toLocaleString()}>
                        {relativeTime(r.createdAt)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-vf-muted">{r.actorUserId}</span>
                    </td>
                    <td className="px-4 py-3">
                      <code className="badge-brand">{r.toolName}</code>
                    </td>
                    <td className="px-4 py-3">
                      <span className={r.outcome === "ok" ? "badge-success" : "badge-danger"}>
                        {r.outcome || "\u2014"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-vf-base">{r.summary || "\u2014"}</span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-vf-muted">
            Showing {page * PAGE_SIZE + 1}--{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
            </button>
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) {
                pageNum = i;
              } else if (page < 3) {
                pageNum = i;
              } else if (page > totalPages - 4) {
                pageNum = totalPages - 7 + i;
              } else {
                pageNum = page - 3 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`btn-sm min-w-[32px] ${page === pageNum ? "btn-primary" : "btn-ghost"}`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Audit;
